import React, { useMemo } from "react";
import { Grid } from "@mui/material";
import Section from "../../../layout/Section";
import Dropdown from "../../../common/inputs/Dropdown";
import { Planstatus, PlanOptionsResponse } from "../../../../types";

interface PlanningPermissionSectionProps {
  regionalplan: Planstatus | undefined;
  flaechennutzungsplan: Planstatus | undefined;
  sonstigesPlanungsverfahren: Planstatus | undefined;
  planOptions: PlanOptionsResponse | null;
  canEdit: boolean;
  onFieldChange: (field: string, value: string | null) => void;
}

/**
 * Planungsrecht-Section des Projektrahmens.
 * Dokumentiert den Verfahrensstand der 3 Planungsdokumente (reine Dokumentation, ohne
 * Einfluss auf den Oberstatus Planungsrecht oder den Reifegrad-Score - siehe
 * ProjectStatusSection für den eigenständigen Planungsrecht-Status).
 * Auswahlwerte werden vom Backend geladen (Single Source of Truth).
 */
const PlanningPermissionSection: React.FC<PlanningPermissionSectionProps> = ({
  regionalplan,
  flaechennutzungsplan,
  sonstigesPlanungsverfahren,
  planOptions,
  canEdit,
  onFieldChange,
}) => {
  const labelWidth = 240;
  const inputWidth = 280;

  const regionalplanOptions = useMemo(
    () => [
      { value: "", label: "– nicht ausgewählt –" },
      ...(planOptions?.regionalplan_options ?? []).map((v) => ({
        value: v,
        label: v,
      })),
    ],
    [planOptions],
  );

  const flaechennutzungsplanOptions = useMemo(
    () => [
      { value: "", label: "– nicht ausgewählt –" },
      ...(planOptions?.flaechennutzungsplan_options ?? []).map((v) => ({
        value: v,
        label: v,
      })),
    ],
    [planOptions],
  );

  const sonstigesPlanungsverfahrenOptions = useMemo(
    () => [
      { value: "", label: "– nicht ausgewählt –" },
      ...(planOptions?.sonstiges_planungsverfahren_options ?? []).map((v) => ({
        value: v,
        label: v,
      })),
    ],
    [planOptions],
  );

  return (
    <Section title="Planungsrecht">
      <Grid container spacing={3} sx={{ py: 1 }}>
        <Grid size={{ xs: 12 }}>
          <Dropdown
            label="Regionalplan (RROP)"
            value={regionalplan ?? ""}
            options={regionalplanOptions}
            onChange={(val) => onFieldChange("regionalplan", val || null)}
            disabled={!canEdit}
            labelWidth={labelWidth}
            inputWidth={inputWidth}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Dropdown
            label="Flächennutzungsplan (F-Plan)"
            value={flaechennutzungsplan ?? ""}
            options={flaechennutzungsplanOptions}
            onChange={(val) =>
              onFieldChange("flaechennutzungsplan", val || null)
            }
            disabled={!canEdit}
            labelWidth={labelWidth}
            inputWidth={inputWidth}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Dropdown
            label="Sonstiges Planungsverfahren"
            value={sonstigesPlanungsverfahren ?? ""}
            options={sonstigesPlanungsverfahrenOptions}
            onChange={(val) =>
              onFieldChange("sonstiges_planungsverfahren", val || null)
            }
            disabled={!canEdit}
            labelWidth={labelWidth}
            inputWidth={inputWidth}
          />
        </Grid>
      </Grid>
    </Section>
  );
};

export default React.memo(PlanningPermissionSection);
